import React, { useCallback, useState, ChangeEventHandler } from "react";

import styles from "./DataUploadForm.module.css";

import { logger } from "src/utils/logger/browser";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import Link from "next/link";
import { Button } from "src/components/ui-components/Button/Button";
import { useSession } from "src/components/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { callUploadedTables } from "src/components/page-components/MyTablesSubmissionsPage";
import { pick } from "lodash";

import { InputFile } from "./InputFile";
import { InputField } from "src/components/ui-components/Input/InputField";
import { Form } from "src/components/ui-components/Form/Form";
import { Toggle } from "src/components/Toggle/Toggle";
import cn from "classnames";
import { IconLoading } from "src/components/ui-components/Icons/IconLoading";
import { HoverPopover } from "src/components/ui-components/HoverPopover/HoverPopover";
import { AnchorButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/AnchorButtonTextOnly";
import { canOwnPrivateDataset } from "./utils";

type DataUploadResponse = {
  status: "SUCCEEDED" | "FAILED";
  namespace: string;
  table_name: string;
};

export const DataUploadForm = ({ onDismiss }: { onDismiss: () => void }) => {
  const [fileName, setFileName] = useState<string>();
  const [fileSize, setFileSize] = useState<number>();
  const [tableName, setTableName] = useState("");
  const [tableNameError, setTableNameError] = useState<string | undefined>();
  const [tableDescription, setTableDescription] = useState("");
  const [dataUploadProcessing, setDataUploadProcessing] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isCompliant, setIsCompliant] = useState(false);

  const session = useSession();
  const activeContext = useActiveContext();
  const privateDatasetsEnabled = canOwnPrivateDataset({
    serviceTierName: activeContext?.serviceTier.name,
    isCustomPlan: activeContext?.serviceTier.hasCustomPlan,
  });

  const onFileUploaded = useCallback((file: File) => {
    setFileName(file.name);
    setFileSize(file.size);
    setTableName(file.name.replace(/\s+/g, "_").replace(/.csv$/, ""));
  }, []);

  const onTableNameChanged: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const value = event.target.value;
      setTableName(value);
      if (/^([a-z]+[a-z0-9_]*[a-z0-9])$/.test(value)) {
        setTableNameError(undefined);
      } else {
        setTableNameError(
          "Table name must only contain lowercase letters, numbers, and underscores. It must not start with a number or underscore or end with an underscore."
        );
      }
    },
    []
  );

  const onFileRemoved = useCallback(() => {
    setFileName(undefined);
    setFileSize(undefined);
    setIsPrivate(false);
    setTableName("");
    setTableNameError(undefined);
    setTableDescription("");
  }, []);

  const onSubmit = async () => {
    if (session && activeContext) {
      setDataUploadProcessing(true);
      try {
        const response = await fetch(
          process.env.NEXT_PUBLIC_DUNE_DATA_UPLOAD_URL ?? "",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.token}`,
            },
            body: JSON.stringify({
              context_owner: pick(activeContext, ["id", "type"]),
              file_name: fileName,
              file_size: fileSize,
              is_private: isPrivate,
              table_name: tableName,
              table_description: tableDescription,
            }),
          }
        );
        if (!response.ok) {
          if (response.status < 500) {
            const { message } = await response.json();
            throw new Error(message);
          } else {
            throw new Error("Please try your data upload again");
          }
        }
        const dataUploadResponse: DataUploadResponse = await response.json();
        if (dataUploadResponse.status !== "SUCCEEDED") {
          throw new Error("Please try your data upload again");
        }
        await callUploadedTables(session, activeContext).catch(() =>
          logger.error("Unable to preload uploaded tables, redirecting...")
        );

        const href =
          activeContext?.type === "user"
            ? "/settings/data"
            : `/settings/teams/manage/${activeContext?.handle}/data`;

        addToastNotification({
          level: "success",
          title: "Data Upload Succeeded",
          description: (
            <AnchorButtonTextOnly href={href} size="M" theme="tertiary">
              View your datasets
            </AnchorButtonTextOnly>
          ),
        });

        setDataUploadProcessing(false);
        onDismiss();
      } catch (error) {
        const description =
          error instanceof Error ? error.message : String(error);
        addToastNotification({
          level: "error",
          title: "Data Upload Failed",
          description,
        });
        setDataUploadProcessing(false);
      }
    }
  };

  const fileUploaded = fileName !== undefined;
  const isValidForm =
    fileUploaded && tableNameError === undefined && isCompliant;
  return (
    <Form onSubmit={onSubmit} className={styles.form} size="M">
      <InputFile
        onFileUploaded={onFileUploaded}
        onFileRemoved={onFileRemoved}
        dataUploadProcessing={dataUploadProcessing}
      />
      <InputField
        size="M"
        style="contained"
        label="Table Name"
        value={tableName}
        placeholder="Enter table name..."
        onChange={onTableNameChanged}
        error={tableNameError}
        disabled={!fileUploaded}
      />

      <InputField
        size="M"
        style="contained"
        label="Table Description"
        value={tableDescription}
        placeholder="Enter description..."
        onChange={(event) => setTableDescription(event.target.value)}
        disabled={!fileUploaded}
      />

      <HoverPopover
        position="above-align-left"
        enabled={!privateDatasetsEnabled}
        content={() => (
          <div className={styles.privateDatasetTooltip}>
            Private data uploads are only available to Premium subscriptions.{" "}
            <Link className={styles.upgradeLink} href="/pricing">
              Upgrade plan
            </Link>
            .
          </div>
        )}
      >
        <div className={cn(styles.privateToggle)}>
          <Toggle
            label="Make table private"
            enabled={isPrivate}
            setEnabled={() => setIsPrivate(!isPrivate)}
            loading={false}
            disabled={!fileUploaded || !privateDatasetsEnabled}
          />
        </div>
      </HoverPopover>
      <div className={styles.privateToggle}>
        <Toggle
          label="I certify this data complies with Dune's T&C (no illegal content, not obtained via hack, etc.)"
          enabled={isCompliant}
          setEnabled={() => setIsCompliant(!isCompliant)}
        />
      </div>
      <div className={styles.buttonGroup}>
        <Button
          type="submit"
          theme="primary"
          size="M"
          loading={false}
          disabled={!isValidForm || dataUploadProcessing}
        >
          Save
        </Button>
        <Button
          type="button"
          theme="tertiary"
          size="M"
          onClick={onDismiss}
          disabled={dataUploadProcessing}
        >
          Cancel
        </Button>
        {dataUploadProcessing && (
          <div className={styles.dataUploadProcessing}>
            <IconLoading />
            <p>Processing... (this may take a few minutes)</p>
          </div>
        )}
      </div>
    </Form>
  );
};
